import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import ArrayChips from '@/components/UI/ArrayChips/ArrayChips';
import Dropdown from '@/components/UI/Dropdown/Dropdown';
import InputText from '@/components/UI/InputText/InputText';
import TableListEditableNew from '@/components/UI/TableListEditable/TableListEditableNew';
import { labelFormat } from '@/containers/CampaignDetail/Description/Description.utils';
import { limitOfVolumeTabsMapping } from '@/helpers/opensearch-utils';
import { reverseObjectKeysAndValues } from '@/helpers/utils';
import useCheckDisabled from '@/hooks/useCheckDisabled';
import useDetectLanguage from '@/hooks/useDetectLanguage';
import {
  copyAffectedAggregate, copyAffectedVehiclesModelDesignation, setAffectedVehiclesValidations,
  setVolumesAndOriginChanged,
} from '@/redux/Campaign/campaign.action';
import {
  getCampaignVehiclesVolumes, getCampaignVehicles, getOriginalValues, getCampaignInfo, getAffectedVehiclesValidations,
  getCampaignValidations, getLimitationPlants,
} from '@/redux/Campaign/campaign.reducer';
import { getVolumesForms } from '@/redux/Forms/forms.reducer';
import { getHeaderLanguage } from '@/redux/Header/header.reducer';
import { getSettingsInfo } from '@/redux/User/user.reducer';
import { validateVehicleVolume } from '../VehicleCleanDirt/VehicleCleanDirt-utils';
import { OPTIONS } from '../volumeDetails-constants';
import {
  handleInputsChanges, getVolumeSelected, getSelectedValues, removeSelected, handleSelectionChanges, getTabInputs,
  getColumnsData, mapPaintCodesList, mapCountryVariantsList, getOriginValuesNewState,
  mapVolumes,
} from '../volumeDetails-utils';
import styles from '../VolumeDetails.module.scss';

const COUNTRY_VARIANTS_TAB = 'COUNTRY_VARIANTS';
const COUNTRY_CODE_COLUMN_ID = 'CountryCode';

const VolumeTabs = (props) => {
  const { volumes, plantSelected, setVolumesOriginhadChanged, originalValues, checkEnabled, setCopyAggreagate,
    headerLanguage, tabSelected, setCopyModelDesignation, enginesModelsList, isReadOnly, volumeForms, settingsLanguage,
    affectedVehiclesValidations, validationActive, validations, setPlantsValidations, limitationPlants } = props;
  const volumesValidations = affectedVehiclesValidations?.[headerLanguage]?.volumes;
  const { t } = useTranslation();
  const { detectLanguage, detectLanguageTranslation } = useDetectLanguage();
  const options = OPTIONS;
  const getList = (list) => (['AGGREGATE', 'VEHICLE_MODEL_DESIGNATION'].includes(tabSelected) ? enginesModelsList?.[tabSelected] : list?.[tabSelected]);
  const handleDelete = (chipToDelete) => {
    removeSelected(getTabInputs(tabSelected)[0].attr, chipToDelete, volumes, plantSelected, setVolumesOriginhadChanged, originalValues);
  };

  const list = useMemo(() => ({
    COUNTRY_VARIANTS: mapCountryVariantsList(volumeForms?.COUNTRY_VARIANTS, settingsLanguage),
    PAINT_CODE: mapPaintCodesList(volumeForms?.PAINT_CODE, settingsLanguage),
  }), [volumeForms?.COUNTRY_VARIANTS, volumeForms?.PAINT_CODE, settingsLanguage]);
  const checkDisabled = useCheckDisabled(true);
  const limitOfVolumeTabsMappingInverse = useMemo(() => reverseObjectKeysAndValues(limitOfVolumeTabsMapping), []);

  const tabValidation = validationActive && volumesValidations?.[plantSelected?.wkzNumber]?.[limitOfVolumeTabsMappingInverse?.[tabSelected]];

  const handleCommentChanges = (label, value) => {
    const newState = mapVolumes(volumes, plantSelected, label, headerLanguage, value);
    setVolumesOriginhadChanged({
      originalValues: getOriginValuesNewState(originalValues, plantSelected),
      volumes: newState,
    });
    validateVehicleVolume(newState, headerLanguage, validations, setPlantsValidations, plantSelected, limitationPlants);
  };

  const mappedTab = Object.entries(limitOfVolumeTabsMapping).find(([, value]) => value === tabSelected)[0];

  const commentValue = getVolumeSelected(getTabInputs(tabSelected)[2].attr, volumes, plantSelected)?.[headerLanguage] || '';

  const onBlur = (name, value) => {
    const label = labelFormat(name);
    detectLanguage(value);

    if (originalValues?.hasChange) {
      detectLanguageTranslation(label, plantSelected.wkzNumber);
    }
  };

  const onSelectionChange = (selectedOptions) => {
    handleSelectionChanges(
      getTabInputs(tabSelected)[0].attr,
      selectedOptions,
      volumes,
      plantSelected,
      setVolumesOriginhadChanged,
      originalValues,
      getList(list),
      tabSelected === 'AGGREGATE' ? setCopyAggreagate : setCopyModelDesignation,
    );
  };

  return (
    <div className={styles.TabContainer}>
      <h4> {t(`affectedVehicles.${tabSelected}`)}</h4>
      <div className={styles.InputsContainer}>
        <div className={styles.OneColumnsWithMarginRight}>
          <Dropdown
            disabled={checkEnabled}
            id={`${tabSelected}-select-selection-according`}
            label={t(getTabInputs(tabSelected)[1].label)}
            options={options}
            value={getVolumeSelected(getTabInputs(tabSelected)[1].attr, volumes, plantSelected) || ''}
            handleChange={(ev) => handleInputsChanges(getTabInputs(tabSelected)[1].attr, ev.target.value, volumes, plantSelected, setVolumesOriginhadChanged, originalValues)}
            language={headerLanguage}
          />
          <div className={styles.SelectedOptions}>
            <div className={styles.Label}> {t(getTabInputs(tabSelected)[0].label)}</div>
            <ArrayChips
              disabled={checkEnabled}
              chipData={getSelectedValues(getTabInputs(tabSelected)[0].attr, volumes, plantSelected)}
              handleDelete={handleDelete}
              fieldPath={['affectedVehicles', 'limitationVolumeManualEntry', mappedTab, 'selections']}
              id={`${tabSelected}-selections`}
            />
          </div>
        </div>
        <div className={styles.OneColumns}>
          <InputText
            disabled={checkEnabled}
            multiline
            maxRows={5}
            rows={5}
            id={`${tabSelected}-comment`}
            invalid={validationActive && tabValidation?.[`${limitOfVolumeTabsMappingInverse?.[tabSelected]}Comment`]?.invalid}
            label={t(getTabInputs(tabSelected)[2].label)}
            value={commentValue}
            handleChange={(ev) => handleCommentChanges(getTabInputs(tabSelected)[2].attr, ev.target.value)}
            onBlur={(ev) => onBlur(`${tabSelected}_COMMENT`, ev.target.value)}
            fieldPath={['limitationVolumeManualEntry', mappedTab, 'comment']}
            maxLength={256}
            helperText={(!checkEnabled && commentValue.length >= 256)
              ? [t('validations.maxLength', { maxLength: 256 })]
              : []}
          />
        </div>
      </div>
      <div className={styles.TableContent}>
        {!isReadOnly && !checkDisabled
          && (
            <TableListEditableNew
              key={`limitation-volume-table-${tabSelected}`}
              columnId={tabSelected === COUNTRY_VARIANTS_TAB ? COUNTRY_CODE_COLUMN_ID : getColumnsData(tabSelected, t).columns[0].name}
              hasCheckbox={!checkEnabled}
              isFiltering
              list={getList(list) || []}
              columns={getColumnsData(tabSelected, t).columns}
              selection={getVolumeSelected(getTabInputs(tabSelected)[0].attr, volumes, plantSelected)}
              onSelectionChange={onSelectionChange}
              hiddenColumns={['TotallyCode', 'HiddenId']}
            />
          )}
      </div>
    </div>
  );
};
const mapStateToProps = (state) => ({
  affectedVehicles: getCampaignVehicles(state),
  volumes: getCampaignVehiclesVolumes(state),
  originalValues: getOriginalValues(state),
  headerLanguage: getHeaderLanguage(state) || getCampaignInfo(state)?.defaultLanguage,
  defaultLanguage: getCampaignInfo(state)?.defaultLanguage,
  volumeForms: getVolumesForms(state),
  settingsLanguage: getSettingsInfo(state)?.language?.toUpperCase(),
  affectedVehiclesValidations: getAffectedVehiclesValidations(state),
  validationActive: getCampaignValidations(state)?.active?.all,
  validations: getCampaignValidations(state),
  limitationPlants: getLimitationPlants(state),
});

const actionCreators = {
  setVolumesOriginhadChanged: setVolumesAndOriginChanged,
  setCopyAggreagate: copyAffectedAggregate,
  setCopyModelDesignation: copyAffectedVehiclesModelDesignation,
  setPlantsValidations: setAffectedVehiclesValidations,

};

export default connect(mapStateToProps, actionCreators)(VolumeTabs);
